import { calcBidPercentage, DateFormats, formatDate } from '@strata/helper-functions';
import { Button } from '@strata/react-buttons';
import { CurrentBid } from '@strata/react-current-bid';
import { FormSection } from '@strata/react-form-section';
import { BidInput } from '@strata/react-inputs';
import { DetailLabel, DetailLabelValuePair } from '@strata/react-label-value-pairs';
import { formatRateString } from '@strata/react-servicing-rate';
import { CountdownTimer } from '@strata/react-timers';
import { validationStrings } from '@strata/strata-ui-strings';
import dayjs from 'dayjs';
import { Form, Formik } from 'formik';
import { useState } from 'react';
import * as Yup from 'yup';

import { BidConfirmationDialog } from 'components/liveReferrals/dialogs/BidConfirmationDialog';
import { ProspectiveReferralInlineMessage } from 'components/shared/ProspectiveReferralInlineMessage';
import { UnitPriceGrid } from 'components/shared/UnitPriceGrid';
import { usePermissionCheck } from 'services/APIHooks';
import { getBidState, useAuctionEnded } from 'services/AuctionHelpers';
import { Permissions } from 'services/constants';
import { useAppDispatch } from 'store';
import { type ReferralDetails } from 'store/api/treatmentApi';
import { type LiveReferral } from 'store/liveReferrals/liveReferralsHooks';
import { submitBid } from 'store/liveReferrals/liveReferralsThunks';

interface BidDetailsProps {
    selectedReferral?: LiveReferral;
    referralDetails?: ReferralDetails;
}

export const BidDetails = ({
    selectedReferral = undefined,
    referralDetails = undefined,
}: BidDetailsProps) => {
    const dispatch = useAppDispatch();
    const writeAuction = usePermissionCheck(Permissions.WRITE_AUCTIONS);
    const [showBidConfirmationDialog, setShowBidConfirmationDialog] = useState(false);

    /** Bid amount that must be exceeded to place another bid. Either user's max bid or the current winning bid */
    const bidThreshold = selectedReferral?.maxBid ?? selectedReferral?.currentBidAmt;

    const bidHelperText = `Enter a bid deeper than ${
        selectedReferral?.maxBid != null ? 'your current max bid of ' : ''
    }${formatRateString({
        bidDecimal: bidThreshold,
    })}.`;

    const auctionEnded = useAuctionEnded(selectedReferral?.auctionEnd);

    // Disable bidding when auction has ended or if user does not have bidding permissions
    const biddingDisabled = auctionEnded || !writeAuction;

    const validationSchema = Yup.object({
        maxBid: BidInput.validator()
            .required(validationStrings.requiredField.errorMessage)
            .lessThan(
                calcBidPercentage(bidThreshold) ?? 0,
                `Bid must be deeper than ${formatRateString({
                    bidDecimal: bidThreshold,
                })}`,
            ),
    });

    return (
        <FormSection
            sectionTitle="Bid Details"
            width="40%"
            content={
                <div className="flex__gap--medium" style={{ flexDirection: 'column' }}>
                    <ProspectiveReferralInlineMessage
                        referralStart={referralDetails?.referralStart}
                        outreachDate={referralDetails?.outreachDate}
                    />
                    <div className="flex__gap--medium">
                        <DetailLabelValuePair
                            style={{ width: '40%' }}
                            label="Current Winning Bid"
                            value={
                                <CurrentBid
                                    bidAmount={selectedReferral?.currentBidAmt}
                                    referentPriceType={selectedReferral?.referentPriceType}
                                    bidState={getBidState(
                                        selectedReferral?.currentBidState,
                                        auctionEnded,
                                    )}
                                />
                            }
                        />
                        <div>
                            <DetailLabel label="Time Remaining" />
                            {selectedReferral && (
                                <CountdownTimer endTime={selectedReferral.auctionEnd} />
                            )}
                        </div>
                    </div>
                    <Formik
                        key={selectedReferral?.referralId} // Reset input when navigating to different auction via notification
                        initialValues={{} as Yup.InferType<typeof validationSchema>}
                        validationSchema={validationSchema}
                        onSubmit={() => setShowBidConfirmationDialog(true)}
                    >
                        {(formik) => (
                            <>
                                <BidConfirmationDialog
                                    isOpen={showBidConfirmationDialog}
                                    onSecondaryButtonClick={() =>
                                        setShowBidConfirmationDialog(false)
                                    }
                                    maxBid={formik.values.maxBid}
                                    selectedReferral={selectedReferral}
                                    referralDetails={referralDetails}
                                    onSubmit={() => {
                                        if (selectedReferral) {
                                            dispatch(
                                                submitBid({
                                                    referralId: selectedReferral.referralId,
                                                    payerId: selectedReferral.payerId,
                                                    segmentId: selectedReferral.segmentId,
                                                    maxBid: formik.values.maxBid,
                                                    referentPriceType:
                                                        selectedReferral?.referentPriceType,
                                                }),
                                            );
                                            setShowBidConfirmationDialog(false);
                                            formik.resetForm();
                                        }
                                    }}
                                    auctionEnded={auctionEnded}
                                    hasPreviouslyBidOnReferral={
                                        selectedReferral?.currentBidState !==
                                        CurrentBid.states.NO_BID
                                    }
                                />
                                <Form autoComplete="off">
                                    <BidInput
                                        id="maxBid"
                                        label="Max Bid"
                                        minAllowedBid={calcBidPercentage(
                                            selectedReferral?.currentBidAmt,
                                        )}
                                        disabled={biddingDisabled}
                                        referentPriceType={selectedReferral?.referentPriceType}
                                        helperText={bidHelperText}
                                    />
                                    <Button
                                        intent={Button.Intent.PRIMARY}
                                        label="Enter Bid"
                                        id="enter-bid-button"
                                        type="submit"
                                        disabled={
                                            biddingDisabled || !formik.dirty || !formik.isValid
                                        }
                                    />
                                    {selectedReferral?.priceIsFrozen && (
                                        <div className="margin__top--x-large">
                                            <UnitPriceGrid
                                                priceFrozenMsg="Servicing unit price will be locked at the servicing start date for the life of this referral."
                                                referralDetails={selectedReferral}
                                                priceDate={formatDate(
                                                    dayjs(),
                                                    DateFormats.HYPHEN_DATE,
                                                )}
                                                currentBid={
                                                    formik.values.maxBid ||
                                                    calcBidPercentage(bidThreshold)
                                                }
                                            />
                                        </div>
                                    )}
                                </Form>
                            </>
                        )}
                    </Formik>
                </div>
            }
        />
    );
};
